<!-- # ---- HTML --- -->
<template>
  <!-- ## SideBar -->
  <v-navigation-drawer v-if="isVisiable" v-model="drawer" temporary :width="400">
    <v-container>
      <v-list>
        <v-list-item prepend-icon="mdi-menu" title="Close Sidebar" @click.stop="drawer = !drawer" />
        <side-bar-items />
      </v-list>
    </v-container>
  </v-navigation-drawer>

  <!-- ## AppBar -->
  <v-app-bar prominent density="compact" :class="{ dev: mode === 'dev', dxc: mode === 'dxc', sta: mode === 'sta' }">
    <template #prepend>
      <v-app-bar-nav-icon v-if="isVisiable" variant="text" @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
      <!-- <v-app-bar-nav-icon @click="toggleDrawer" class="appbar-button-toggle-sidebar" /> -->
      <v-btn>
        <router-link to="/">
          <div class="appbar-area-logo">
            <v-img src="../assets/Suzuki_logo.svg" class="logo-navibar" />
          </div>
        </router-link>
        <!-- <v-icon>mdi-home</v-icon> -->
      </v-btn>
    </template>
    <template #default>
      <v-app-bar-title class="app-bar-title">
        <v-col>
          <v-row>{{ appName }}</v-row>
          <!-- <v-row>{{ pageTitle }}</v-row> -->
          <v-row>{{ appStore.pageTitle }}</v-row>
        </v-col>
      </v-app-bar-title>
    </template>
    <template #append>
      <v-card v-if="isVisiable" class="appbar-area-user-info">
        <v-row class="pa-0 ma-0" cols="12">
          <v-spacer></v-spacer>
          <!-- <v-divider vertical class="mx-3" thickness="2"></v-divider> -->
          <v-col class="col-user-info pa-1 ma-2" cols="5">
            <v-row>{{ authStore.getUserInfo().employeeName }} [{{ authStore.getUserInfo().roleName }}]</v-row>
            <v-row>{{ authStore.getUserInfo().employeeId }}</v-row>
          </v-col>
          <v-divider vertical class="mx-3" thickness="2"></v-divider>

          <v-col class="col-sub-info pa-1 ma-2" cols="5">
            <!-- <v-row>PRCTCCCD</v-row> -->
            <v-row>
              <!-- <label for="area-prctcc-cd">PRCTCC CD: </label> -->
              PRCTCC CD:
              <v-select
                v-model="authStore.getUserInfo().accessPrctCode"
                class="area-prctcc-cd elevation-0 compact"
                :hide-details="true"
                hide-spin-buttons
                :items="authStore.getUserInfo().accessPrctCodeList"
                density="compact"
                variant="solo"
              />
            </v-row>
            <v-row class="show-datetime">{{ currentDateTime }}</v-row>
          </v-col>
        </v-row>
      </v-card>
    </template>
  </v-app-bar>
</template>

<!-- # ---- Script --- -->
<script lang="ts">
import SideBarItems from '@/components/SideBarltems.vue';
import { useRouter } from '@/router/use-router';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { clearMessage } from '@/utils/messageUtils';
import storage from '@/utils/storage';
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, toRefs, watch } from 'vue';

export default defineComponent({
  name: 'SideAndAppBar',
  components: { SideBarItems },
  setup() {
    const appStore = useAppStore();
    const authStore = useAuthStore();
    const router = useRouter();

    let timer: any = null;

    // console.log(store_userInfo.username);
    // 初期化時に現在の document.title をストアに反映
    appStore.setPageTitle(document.title);

    // MutationObserver を使用して document.title の変更を監視
    const observer = new MutationObserver(() => {
      appStore.setPageTitle(document.title);
    });

    const config = { childList: true, subtree: true };
    observer.observe(document.querySelector('title') as HTMLTitleElement, config);

    const status = reactive({
      drawer: false, // サイドバーの初期状態
      currentDateTime: '',
      appName: 'PROD-PxP', // アプリ名の初期値
      mode: 'local', // 初期モード
    });

    function updateTime() {
      const now = new Date();
      const dateOptions: any = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      };
      const timeOptions: any = {
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      };

      const dateString = now.toLocaleDateString('ja-JP', dateOptions).replace(/\//g, '.');
      const timeString = now.toLocaleTimeString('ja-JP', timeOptions);

      status.currentDateTime = `${dateString} ${timeString}`;
    }

    updateTime();
    // 次の00秒までのミリ秒数を計算
    const now = new Date();
    const delay = (60 - now.getSeconds()) * 1000;

    setTimeout(() => {
      updateTime();
      timer = setInterval(updateTime, 60000); // 1分ごとに更新
    }, delay);

    //受注国コード変更の場合(accessPrctCode)
    watch(
      () => authStore.getUserInfo().accessPrctCode,
      (newValue, oldValue) => {
        if (newValue !== oldValue && newValue && oldValue) {
          storage.setItem('pxp_userInfo', JSON.stringify(authStore.getUserInfo()));
          clearMessage();
          // 受注国コードが変更された場合、トップページにリダイレクト
          router.push({ path: '/TopPage', query: { _r: Date.now() } });
        }
      },
      { immediate: true }, // 初期値も監視する
    );

    // コンポーネントの破棄時に observer を停止
    onBeforeUnmount(() => {
      observer.disconnect();
      if (timer) {
        //タイマーをクリア
        clearInterval(timer);
      }
    });

    onMounted(() => {
      status.mode = import.meta.env.MODE || 'dev';
      status.mode = status.mode === 'development' ? 'dev' : status.mode;
      status.appName = ['dev', 'dxc', 'sta'].includes(status.mode)
        ? `【${status.mode.toUpperCase()}】PROD-PxP`
        : 'PROD-PxP';
    });

    return {
      authStore,
      appStore,
      ...toRefs(status),
      updateTime,
      isVisiable: computed(() => authStore.getUserInfo().employeeName !== ''),
    };
  },
});
//
</script>

<!-- # ---- Style --- -->
<style scoped lang="scss">
.app-bar-title * {
  font-size: 1.2rem;
}

.appbar-area-logo {
  width: 8.25rem;

  img {
    max-height: 100%;
    max-width: 100%;
    vertical-align: center;
  }
}

header.v-app-bar {
  padding: 0.33rem;
  vertical-align: initial;
  box-shadow: none !important;
  border-width: 0.1rem;
}

header.dev,
header.dxc {
  background: #009688;
}
header.sta {
  background: #e20a0a;
}

.v-list-group__items {
  padding-left: 1.5rem;
}

.appbar-area-user-info {
  margin: 0.5rem;
  box-shadow: none;
  width: 30rem;
  height: 3rem;

  * {
    text-wrap: nowrap;
  }

  .col-sub-info {
    // width: 17rem;
    .area-prctcc-cd {
      width: 6rem !important;
      height: 1.5rem !important;
      min-height: fit-content !important;
      margin: 0 0.5rem !important;
      padding: 0 0.5rem !important;
      box-shadow:
        0px 3px 1px -2px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
        0px 2px 2px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
        0px 1px 5px 0px var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12));
    }
  }
}

.dev .appbar-area-user-info,
.dxc .appbar-area-user-info {
  background: #009688;
}
.sta .appbar-area-user-info {
  background: #e20a0a;
}

.dev :deep(.v-field),
.dxc :deep(.v-field) {
  background: #009688;
}

.sta :deep(.v-field) {
  background: #e20a0a;
}
</style>
